/**
 * ------------------------------------------------------------------------------------------------------------
 * ARCHITECTURAL NOTE:
 * KHÔNG sử dụng file index.ts (Barrel) trong thư mục @core để tránh Circular Dependency (NG0200).
 * Luôn import trực tiếp từ file nguồn.
 * ------------------------------------------------------------------------------------------------------------
 */
import {Injectable} from '@angular/core';
import moment from "moment";
import 'moment/locale/vi';
import {BehaviorSubject} from "rxjs";
import {environment} from "@environments/environment";
import {TranslocoService} from "@jsverse/transloco";
// CHÚ Ý: Tại các service cốt lõi, luôn import trực tiếp từ file (ví dụ: ../../services/api.service),
// KHÔNG import từ "@core/services" (barrel) để tránh lỗi Circular Dependency (NG0200).
import {ApiService} from "../../services/api.service";
import {LocalStorageService} from "../../services/storage.service";
import {AuthService} from "../../authentication/auth.service";
import {STORAGE_KEY} from "@core/constants";
import {ISelectOption} from "@core/models";

import {NotifyService} from "@shared/services/global";
import {
  IBusinessI18nConfig,
  LANGUAGE_ITEM,
  LOCALE_CODE,
  LOCALE_TO_MAPPING_SERVER,
  PERCENT_DECIMAL_DEFAULT
} from '@core/libs/i18n/business-i18n.model';

@Injectable({
  providedIn: 'root',
})
export class BusinessI18nService {
  isLoadLocaleConfig = false;

  private languageSubject = new BehaviorSubject<LOCALE_CODE>(
    environment.language.code as LOCALE_CODE
  )
  language$ = this.languageSubject.asObservable();

  private configsSubject = new BehaviorSubject<IBusinessI18nConfig>(this.getConfigDefault())
  configs$ = this.configsSubject.asObservable();

  constructor(
    private _transLoco: TranslocoService,
    private _apiService: ApiService,
    private notifyService: NotifyService,
    private localStorage: LocalStorageService,
    private authService: AuthService,
  ) {
    this.initialize();
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * Init ------------------------------------------------------------------------------------------------------
   * ------------------------------------------------------------------------------------------------------------
   * */
  private initialize() {
    const langDefault = this.localStorage.getPure(STORAGE_KEY.langDefault);

    // Sử dụng giá trị mặc định nếu `langDefault` không hợp lệ
    const language = langDefault && typeof langDefault === 'string' ? langDefault : environment.language.code;
    this.setLanguage(language, true);
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * Locale ---------------------------------------------------------------------------------------------------
   * ------------------------------------------------------------------------------------------------------------
   * */
  getLocaleConfigs(): IBusinessI18nConfig {
    return this.configsSubject.value;
  }

  setLocaleConfigs(newConfigs: IBusinessI18nConfig): void {
    this.configsSubject.next(newConfigs);
  }

  getLocale(): string {
    const {locale} = this.getLocaleConfigs()
    return locale;
  }

  private getConfigDefault(): IBusinessI18nConfig {
    return {
      locale: 'vi-VN',
      currency: {
        code: 'VND',
        display: '',
      },
      timezone: 'Asia/Ho_Chi_Minh',
      percentDecimals: PERCENT_DECIMAL_DEFAULT
    }
  }

  private _getLocaleCodeMapping(locale: string) {
    return LOCALE_TO_MAPPING_SERVER[locale.toLowerCase()] as LOCALE_CODE;
  }

  /**
   * ------------------------------------------------------------------------------------------------------------
   * API ---------------------------------------------------------------------------------------------------
   * ------------------------------------------------------------------------------------------------------------
   * */
  // Load cài đặt locale DN
  loadBusinessLocaleSettings() {
    if (this.isLoadLocaleConfig) {
      return;
    }
    // @TODO NVN Example xử lý call API tại đây

    // const params = {
    // tab: 'getBusinessLocaleSettings',
    // businessId: this.authService.businessCurrent?.id,
    // }
    // const uri = `${ uriConsts.store.MANAGE_LOAD }?tab=getBusinessLocaleSettings`
    // this._apiService.postRequest<IResponseApi<IBusinessLocaleSettings>>(uri, params).subscribe({
    // next: res => {
    // if (res.code) {
    // const setting = res.data;
    //
    // this.setLocaleConfigs({
    // locale: this._getLocaleCodeMapping(setting.country),
    // currency: {
    // code: setting.currency.code,
    // display: setting.currency.display,
    // },
    // timezone: setting.timezone
    // })
    // }
    // },
    // complete: () => {
    // this.isLoadLocaleConfig = true;
    // }
    // })
  }


  /**
   * ------------------------------------------------------------------------------------------------------------
   * Language ---------------------------------------------------------------------------------------------------
   * ------------------------------------------------------------------------------------------------------------
   * */
  setLanguage(code: string, isDefault = false) {
    if (!code) {
      code = environment.language.code;
    }

    // Map mã ngôn ngữ cho moment (ví dụ: vi-VN -> vi, en-US -> en)
    const momentLocale = code.split('-')[0].toLowerCase();
    moment.locale(momentLocale);

    this.languageSubject.next(code as LOCALE_CODE);
    this._transLoco.setActiveLang(code);
    this.localStorage.setPure(STORAGE_KEY.langDefault, code);
  }

  getLanguages(): ISelectOption[] {
    return environment.language.availableLangs.map(item => {
      return {
        value: item.id,
        label: item.label,
      }
    })
  }

  getLanguage(): LANGUAGE_ITEM | undefined {
    return environment.language.availableLangs.find(l => l.id == this.getLanguageCode())
  }

  getLanguageCode(): LOCALE_CODE {
    return this.languageSubject.value;
  }

}
